// SemanticRoutingControl.tsx
import { useState, useCallback, Dispatch, SetStateAction } from "react";
import { Checkbox, ICheckboxProps, Stack } from "@fluentui/react";
import { initializeSemanticRouter } from "../../api";
import { useId } from "@fluentui/react-hooks";
import { HelpCallout } from "../../components/HelpCallout";
import { useLogin, getToken } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { SystemTemplate } from "../../api";

interface SemanticRoutingProps {
  useSemanticRouting: boolean;
  setUseSemanticRouting: Dispatch<SetStateAction<boolean>>;
  setTemplates?: (templates: SystemTemplate[]) => void;
  setIsRouterInitialized: Dispatch<SetStateAction<boolean>>;
}

export const SemanticRoutingControl: React.FC<SemanticRoutingProps> = ({
  useSemanticRouting,
  setUseSemanticRouting,
  setTemplates,
  setIsRouterInitialized,
}) => {
  const { t } = useTranslation();
  const client = useLogin ? useMsal().instance : undefined;
  const [isInitializing, setIsInitializing] = useState<boolean>(false);

  const semanticRoutingId = useId("semanticRouting");
  const semanticRoutingFieldId = useId("semanticRoutingField");

  const handleSemanticRoutingChange = async (
    _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean
  ) => {
    if (checked) {
      setIsInitializing(true);
      try {
        const idToken = client ? await getToken(client) : undefined;
        const response = await initializeSemanticRouter(idToken);

        if (!response.ok) {
          throw new Error("Failed to initialize semantic routing");
        }

        const data = await response.json();
        if (
          setTemplates &&
          data.final_templates &&
          Array.isArray(data.final_templates)
        ) {
          setTemplates(data.final_templates);
        }

        setIsRouterInitialized(true);
        setUseSemanticRouting(true);
      } catch (error) {
        console.error("Error initializing semantic routing:", error);
        setUseSemanticRouting(false);
        setIsRouterInitialized(false);
        if (setTemplates) {
          setTemplates([]);
        }
      } finally {
        setIsInitializing(false);
      }
    } else {
      setUseSemanticRouting(false);
      setIsRouterInitialized(false);
      if (setTemplates) {
        setTemplates([]);
      }
    }
  };

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      <Checkbox
        id={semanticRoutingFieldId}
        checked={useSemanticRouting}
        label={t("labels.useSemanticRouting")}
        onChange={handleSemanticRoutingChange}
        disabled={isInitializing}
        aria-labelledby={semanticRoutingId}
        onRenderLabel={(props: ICheckboxProps | undefined) => (
          <HelpCallout
            labelId={semanticRoutingId}
            fieldId={semanticRoutingFieldId}
            helpText={t("helpTexts.useSemanticRouting")}
            label={props?.label}
          />
        )}
      />
    </Stack>
  );
};
